import SelectionArea from "@viselect/vanilla";
import { arrayUnique } from "common/utilities/core/array";
import type { EntitySequence as Sequence, SequenceAlign } from "sequenceAnalyser/api/sequenceAnalyserApiClient";

export interface SequenceSelection {
  /** SequenceAlign ids */
  ids: number[];
  /** Last SequenceAlign ID that has been added or removed (might not be part of the list) */
  lastId?: number;
  /** Starts at 0 (UI shows 1). If positionStart/positionEnd is missing, it means the whole row is selected */
  positionStart?: number;
  positionEnd?: number;
}

export interface SequenceSelectionWithSequenceData extends SequenceSelection {
  createdDate: string;
  rawSequences: Sequence[];
  sequences: Sequence[];
  alignedSequences: SequenceAlign[];
}

function getSelectedElements() {
  return Array.from(document.querySelectorAll(".selectable.selected"));
}

function getSelectionOverlayElement() {
  return document.getElementById("selectionOverlay");
}

function createSelectionOverlayElement() {
  const overlay = document.createElement("div");
  overlay.id = "selectionOverlay";
  document.body.append(overlay);
  return overlay;
}

function createSelectionOverlay(selected: Element[]) {
  // Save edge of the selection
  const edge: EdgeSelection = {};

  selected.forEach((el) => {
    const { top: offsetTop, left: offsetLeft, width, height } = el.getBoundingClientRect();

    if (!edge.top || offsetTop < edge.top) {
      edge.top = offsetTop;
    }

    if (!edge.right || offsetLeft > edge.right) {
      edge.right = offsetLeft;
    }

    if (!edge.bottom || offsetTop > edge.bottom) {
      edge.bottom = offsetTop;
    }

    if (!edge.left || offsetLeft < edge.left) {
      edge.left = offsetLeft;
    }

    edge.elementWidth = width;
    edge.elementHeight = height;
  });

  // Create an overlay over the selection to have border / background
  const overlay = getSelectionOverlayElement() || createSelectionOverlayElement();

  if (edge.top && edge.right && edge.bottom && edge.left && edge.elementHeight && edge.elementWidth) {
    overlay.style.top = `${edge.top + window.scrollY}px`;
    overlay.style.left = `${edge.left + window.scrollX}px`;
    overlay.style.width = `${edge.right - edge.left + edge.elementWidth}px`;
    overlay.style.height = `${edge.bottom - edge.top + edge.elementHeight}px`;
  }
}

/**
 * Highlight HTML residues elements that are part of the selection
 * by adding the "selected" class
 */
export function markResiduesFromSelectionAsSelected(selection: SequenceSelection) {
  // Early return if no range is defined
  if (selection.positionStart === undefined || selection.positionEnd === undefined) {
    createSelectionOverlay(getSelectedElements());
    return;
  }

  // Skip the work if no IDs
  if (selection.ids.length === 0) {
    return;
  }

  // Get all existing selected elements to avoid reselecting them
  const existingSelected = new Set();
  document.querySelectorAll(".selected").forEach((el) => {
    const dataSequence = (el as HTMLElement).dataset.sequence;
    const dataPosition = (el as HTMLElement).dataset.position;
    if (dataSequence && dataPosition) {
      existingSelected.add(`${dataSequence}-${dataPosition}`);
    }
  });

  // Skip adding .selected to elements that already have it
  // Use this approach only when there are many existing selected elements
  const shouldCheckExisting = existingSelected.size > 100;

  // Rather than storing entire position arrays, just work with start/end
  const start = selection.positionStart;
  const end = selection.positionEnd;

  // ===== CRITICAL OPTIMIZATION =====
  // Instead of creating selectors for every ID/position combination and doing multiple
  // querySelectorAll calls, do ONE query with an optimized selector approach
  const idSelectors = selection.ids
    .map((id) => {
      return `[data-sequence="${id}"]`;
    })
    .join(", ");

  // Get all elements for these IDs in one go
  const allElementsForIds = document.querySelectorAll(idSelectors);

  // Filter and process matching elements directly - much faster than multiple DOM queries
  const elementsToSelect: HTMLElement[] = [];

  // Process in batches for better UI responsiveness
  const processBatch = (startIdx: number, endIdx: number, callback: () => void) => {
    for (let i = startIdx; i < endIdx; i++) {
      const el = allElementsForIds[i] as HTMLElement;
      const position = Number(el.dataset.position);

      // Only process if within our position range
      if (position >= start && position <= end) {
        const key = `${el.dataset.sequence}-${position}`;

        // Skip if already selected (if we're checking)
        if (shouldCheckExisting && existingSelected.has(key)) {
          continue;
        }

        elementsToSelect.push(el);
      }
    }

    if (endIdx < allElementsForIds.length) {
      // Schedule next batch
      setTimeout(() => {
        processBatch(endIdx, Math.min(endIdx + 1000, allElementsForIds.length), callback);
      }, 0);
    } else {
      // All batches processed, now add classes
      requestAnimationFrame(() => {
        elementsToSelect.forEach((el) => {
          el.classList.add("selected");
        });

        // Create overlay at the end, after all classes are added
        createSelectionOverlay(getSelectedElements());

        // Done processing
        callback();
      });
    }
  };

  // Start batch processing
  processBatch(0, Math.min(1000, allElementsForIds.length), () => {});
}

/**
 * Remove the .selected class from all selectable residues.
 * Ensure we remove the existing selection and overlay that was added manually via markResiduesFromSelectionAsSelected.
 */
function unSelectAllResidues(area: SelectionArea) {
  getSelectedElements().forEach((el) => {
    el.classList.remove("selected");
  });

  // Clear previous selection
  area.clearSelection(true, true);

  // Remove overlay
  getSelectionOverlayElement()?.remove();
}

let selectionInstance: SelectionArea | undefined;
/** Ref to the element that a selection holding shift starts with */
let startElement: { rowId: number; position: number } | undefined;

interface EdgeSelection {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
  elementWidth?: number;
  elementHeight?: number;
}

export function initSequenceResiduesSelection(
  selection: SequenceSelection,
  onSelect: (newSelection?: SequenceSelection) => void,
) {
  // Keep track of whether we've processed a click event
  let clickProcessed = false;

  selectionInstance = new SelectionArea({
    // Class for the selection-area-element
    selectionAreaClass: "sequences",

    behaviour: {
      // px, how many pixels the point should move before starting the selection (combined distance).
      // Or specify the threshold for each axis by passing an object like {x: <number>, y: <number>}.
      startThreshold: { x: 0, y: 0 },
    },

    features: {
      // Disable the selection functionality for touch devices
      touch: false,
    },

    // Query selectors from elements which can be selected
    selectables: [".selectable"],

    // Query selectors for elements which will be used as boundaries for the selection
    boundaries: ["#sequences"],
  });

  selectionInstance.on("beforestart", (e) => {
    // When dragging start, ensure we are hovering a 'selectable' element
    // and not an element from the sidebar
    if (e.event && e.event.target) {
      // Reset the click processed flag when starting a drag
      clickProcessed = false;

      // Clear the current selection unless the shift key is pressed
      if (!e.event.shiftKey) {
        startElement = undefined;
        unSelectAllResidues(e.selection);
      }

      const classes = (e.event.target as HTMLElement).classList;
      return classes.contains("selectable");
    }
    return true;
  });

  selectionInstance.on(
    "move",
    ({
      event,
      store: {
        selected,
        changed: { removed, added },
      },
    }) => {
      // Add a custom class to the elements that where selected
      added.forEach((el) => el.classList.add("selected"));

      // Remove the class from elements that where removed since the last selection
      if (!event?.shiftKey) {
        removed.forEach((el) => el.classList.remove("selected"));
      }

      // Create an overlay (background/border)
      createSelectionOverlay(selected);
    },
  );

  selectionInstance.on("stop", ({ event, selection: selectionOnStop, store: { selected, stored } }) => {
    // Don't process this event if it was the result of a click we already processed
    if (clickProcessed) {
      clickProcessed = false;
      return;
    }

    if (event?.shiftKey === undefined) return;

    if (selected.length > 0) {
      const newSelection: SequenceSelection = {
        ids: [],
      };

      if (event?.shiftKey && stored.length > 1) {
        // Elements selected holding shift key: square range selection
        const selectedRowIds = arrayUnique(stored.map((s) => Number((s as HTMLElement).dataset.rowId)));
        const firstRowId = Math.min(...selectedRowIds);
        const lastRowId = Math.max(...selectedRowIds);

        // Select the lowest and highest position for the first/last rows
        stored.forEach((el) => {
          const { dataset } = el as HTMLElement;
          const position = Number(dataset.position) || 0;

          newSelection.ids.push(Number(dataset.sequence));

          if (Number(dataset.rowId) === firstRowId) {
            if (newSelection.positionStart === undefined || newSelection.positionStart > position) {
              newSelection.positionStart = position;
            }
          }
          if (Number(dataset.rowId) === lastRowId) {
            if (newSelection.positionEnd === undefined || newSelection.positionEnd < position) {
              newSelection.positionEnd = position;
            }
          }
        });

        // Ensure the position is in the right order (user might select a square from all around the first central selected residue)
        if (
          newSelection.positionStart !== undefined &&
          newSelection.positionEnd !== undefined &&
          newSelection.positionStart > newSelection.positionEnd
        ) {
          const { positionStart } = newSelection;
          newSelection.positionStart = newSelection.positionEnd;
          newSelection.positionEnd = positionStart;
        }
      } else {
        // One element or multiple via dragging
        selected.forEach((el) => {
          const { dataset } = el as HTMLElement;
          const position = Number(dataset.position) || 0;

          newSelection.ids.push(Number(dataset.sequence));

          if (newSelection.positionStart === undefined) newSelection.positionStart = position;
          newSelection.positionStart = newSelection.positionStart < position ? newSelection.positionStart : position;

          if (newSelection.positionEnd === undefined) newSelection.positionEnd = position;
          newSelection.positionEnd = newSelection.positionEnd > position ? newSelection.positionEnd : position;
        });
      }

      newSelection.ids = arrayUnique(newSelection.ids);

      if (event?.shiftKey && startElement) {
        // Unselect all since the library is not performing a square range selection by default but 'from to'
        unSelectAllResidues(selectionOnStop);

        // Only preserve the first elements selected in the store
        // so if the user keep holding shift and select another element, it will create a
        // square range selection between those two elements
        const selectionFirstElement = document.querySelector(
          `[data-row-id="${startElement.rowId}"][data-position="${startElement.position}"]`,
        ) as Element;

        selectionOnStop.select([selectionFirstElement], true);
      } else {
        selectionOnStop.select(selected, true);
      }

      if (!event?.shiftKey && newSelection.positionStart !== undefined) {
        // Select the top left first element to be stored as a reference for shift selection
        startElement = {
          rowId: Number((selected[0] as HTMLElement).dataset.rowId),
          position: newSelection.positionStart,
        };
      }

      onSelect(newSelection);
    } else {
      unSelectAllResidues(selectionOnStop);
      onSelect(undefined);
    }
  });

  // Add click handler for click selection
  const sequencesContainer = document.querySelector("#sequences");
  if (sequencesContainer) {
    sequencesContainer.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;

      // Only process clicks directly on selectable elements
      if (target && target.classList.contains("selectable") && selectionInstance != undefined) {
        // Mark that we've processed a click event
        clickProcessed = true;

        // Select the clicked element
        target.classList.add("selected");

        // Create selection object for the clicked element
        const newSelection: SequenceSelection = {
          ids: [Number(target.dataset.sequence)],
          positionStart: Number(target.dataset.position),
          positionEnd: Number(target.dataset.position),
        };

        // Update the start element for potential shift selections
        startElement = {
          rowId: Number(target.dataset.rowId),
          position: Number(target.dataset.position),
        };

        // Store the element in the selection instance for future operations
        selectionInstance.select([target], true);

        // Create overlay for the selected element
        createSelectionOverlay([target]);

        // Call the onSelect callback
        onSelect(newSelection);
      }
    });
  }

  // Ensure the selection is highlighted after the user switch tabs
  markResiduesFromSelectionAsSelected(selection);
}

export function clearResiduesSelection() {
  if (selectionInstance) {
    unSelectAllResidues(selectionInstance);
  }

  startElement = undefined;
}

export function selectAlignedSequenceRangeIds(alignedSequences: SequenceAlign[], fromId: number, toId: number) {
  const startIndex = alignedSequences.findIndex((s) => s.id === fromId);
  const endIndex = alignedSequences.findIndex((s) => s.id === toId);
  if (endIndex > startIndex) {
    return alignedSequences.slice(startIndex, endIndex + 1).map((s) => s.id);
  }
  return alignedSequences.slice(endIndex, startIndex + 1).map((s) => s.id);
}

/**
 * Toggle the visibility of selections without expensive DOM operations
 * This is much faster than clearing and re-applying selections
 */
export function toggleSelectionVisibility(visible: boolean): void {
  // Get the overlay element
  const overlay = document.getElementById("selectionOverlay");

  if (overlay) {
    // Set display style - much faster than recreating the overlay
    overlay.style.display = visible ? "block" : "none";
  }

  // Optionally, you could also toggle a CSS class on a parent element
  // to hide all selected elements via CSS instead of JS
  const sequencesContainer = document.getElementById("sequences");
  if (sequencesContainer) {
    sequencesContainer.classList.toggle("hide-selection", !visible);
  }
}
